<script setup>

import Button from "@/Components/Button.vue";
import Card from "@/Components/Card.vue";
import {router, usePage} from "@inertiajs/vue3";
import InputError from "@/Components/InputError.vue";
import {computed, ref} from "vue";
import Multiselect from 'vue-multiselect/dist/vue-multiselect.ssr.js'
import PaymentMethod from "@/Pages/Offer/Components/PaymentMethod.vue";
import Checkbox from "@/Components/Checkbox.vue";
import {priceNumToStr, priceStrToNum} from "@/helper.js";
import floor from "lodash-es/floor";
import ceil from "lodash-es/ceil";

const props = defineProps({
    price: Number
})

const page = usePage()
const selectPaymentMethods = ref(undefined)
const paymentMethods = page.props.payment_methods,
    useWallet = ref(page.props.wallet_balance > 0)

const deductWalletAmount = ref(0)

const getDeductWalletBalanceString = () => priceNumToStr(deductWalletAmount.value * page.props.currency.rate)

const deductWalletAmountString = ref(getDeductWalletBalanceString())
const deductWalletAmountUpdate = (event) => {
    deductWalletAmount.value = floor(
        priceStrToNum(event.target.value) / page.props.currency.rate * 10 ** page.props.currency.precision, 0)
}

const pay = () => {
    router.post(route('orders.store', {offer: page.props.offer.id}), {
        wallet_amount: deductWalletAmount.value,
        method: selectPaymentMethods.value?.value
    })
}

const walletMaxAmount = computed(() => {
    return page.props.wallet_balance > props.price ? props.price : page.props.wallet_balance
})
const selectMaxAmount = () => {
    deductWalletAmount.value = walletMaxAmount.value
    deductWalletAmountString.value =
        priceNumToStr(ceil(deductWalletAmount.value * page.props.currency.rate) / 10 ** page.props.currency.precision)
}

const minPaymentAmount = computed(() => Math.floor((selectPaymentMethods.value?.min_amount / 100) * selectPaymentMethods.value.currency_rate));

</script>

<template>
    <card class="flex px-3 py-5 flex-col gap-2.5 w-full">
        <div class="px-2.5 flex gap-1.5 items-end">
            <span class="text-sm dark:text-slate-200 font-medium uppercase leading-full">{{ $t('Price') }}</span>
            <div class="flex items-end gap-1 leading-full">
                <span class="text-xl dark:text-slate-200 font-medium leading-full ">{{ $page.props.offer.price_currency_formatted }}</span>
            </div>
        </div>
        <div class="px-2.5 sm:px-0 flex gap-1.5 items-end my-2.5">
            <div class="px-2.5 dark:bg-deep-green py-2 bg-green-50 w-full flex items-stretch content-center rounded-lg">
                <div class="w-[24px]">
                    <ion-icon class="text-2xl fill-green-600" name="shield-checkmark" style="width: 24px; height: 24px"></ion-icon>
                </div>

                <div class="ml-2.5">
                    <p class="text-sm font-medium dark:text-slate-200 font-sans leading-[.875rem]">
                        {{ $t('Your money is safe') }}
                    </p>
                    <p class="text-sm font-sans dark:text-slate-200 leading-[.875rem]">
                        {{ $t('The seller will not receive payment until you confirm the fulfillment of the order.') }}
                    </p>
                </div>
            </div>
        </div>
        <Multiselect
            v-model="selectPaymentMethods" :close-on-select="true" :options="paymentMethods"
            :placeholder="$t('Select payment method')" :searchable="false"
            :show-labels="false" track-by="value"
            class="border-gray-300 focus:border-blue-600 focus:ring-blue-600 rounded-md shadow-sm"
        >
            <!--suppress VueUnrecognizedSlot -->
            <template #option="props">
                <PaymentMethod :props="props"/>
            </template>
            <!--suppress VueUnrecognizedSlot -->
            <template #placeholder>
                <span class="font-sans">
                    {{ $t('Select payment method') }}
                </span>
            </template>
            <!--suppress VueUnrecognizedSlot -->
            <template #singleLabel="props">
                <PaymentMethod :props="props"/>
            </template>
        </Multiselect>
        <div v-if="selectPaymentMethods?.min_amount > page.props.offer.price">
            <div class="px-2.5 py-2 w-full flex items-stretch content-center rounded-lg">
                <div class="w-[16px]">
                    <ion-icon class="text-base fill-green-600" name="alert-circle" style="width: 16px; height: 16px"></ion-icon>
                </div>
                <div class="ml-1.5">
                    <p class="text-sm font-sans text-slate-500 dark:text-slate-400 leading-[.875rem]">
                        {{$t('The minimum amount of payment by this method is :sum. The difference will be credited to your wallet for future payments.', {
                            sum: `${ minPaymentAmount } ${selectPaymentMethods.currency.toUpperCase()}`
                        })}}
                    </p>
                </div>
            </div>
        </div>
        <div v-if="page.props.wallet_balance > 0" class="px-2.5 pb-3 flex gap-2 items-center">
            <Checkbox :checked="useWallet"
                      @update:checked="useWallet = !useWallet"
            >
                <template #label>
                    <span class="dark:text-slate-200">{{$t('Use funds from the wallet')}}</span>
                </template>
            </Checkbox>
        </div>
        <template v-if="useWallet">
            <div class="px-1 flex justify-between items-center">
                <div class="text-sm text-slate-500 dark:text-slate-400">{{ $t('Available balance:') }}
                    {{ page.props.wallet_balance_formatted }}
                </div>
                <button class="bg-blue-600 hover:bg-blue-700 text-white dark:text-dark-blue rounded-full h-7 px-4 uppercase text-[0.65rem]"
                        @click="selectMaxAmount()">
                    MAX
                </button>
            </div>
            <div class="relative">
                <input
                    v-model="deductWalletAmountString"
                    :class="[deductWalletAmount > walletMaxAmount ? 'border-red-300 focus:border-red-300 focus:ring-red-300'
                : 'border-gray-300 focus:border-blue-600 focus:ring-blue-600']"
                    :placeholder="$t('Enter the amount to be debited from your wallet')"
                    class="rounded-md dark:bg-deep-blue dark:text-slate-200 shadow-sm placeholder:text-gray-400 w-full pl-8"
                    type="text"
                    @input="deductWalletAmountUpdate"
                >
                <div class="absolute inset-y-0 left-0 flex items-center pl-4">
                    <span class="text-gray-400 font-normal text-xl">{{ page.props.currency.symbol }}</span>
                </div>
            </div>
            <div v-if="deductWalletAmount > walletMaxAmount" class="text-red-500 text-sm mt-1 pl-2">
                <ion-icon name="alert-circle" class="inline-block mr-1 align-middle"></ion-icon>
                <span class="text-sm align-middle">{{ $t('The amount exceeds the wallet balance') }}</span>
            </div>
        </template>

        <InputError v-if="page.props.errors?.error" :message="page.props.errors?.error"/>
        <div class="flex pt-1 justify-center px-2.5">
            <Button :disabled="!selectPaymentMethods && deductWalletAmount !== props.price"
                    :text="$t('order')"
                    class="text-sm w-full"
                    type="primary"
                    @click="pay()"
            ></Button>
        </div>
    </card>
</template>
